import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../../styles/theme';
import type { FanChipBarProps } from './useFanChoice';

interface Props extends FanChipBarProps {
  /** Horizontal inset, so the chips line up with the rows of the sheet or drawer. */
  inset: number;
  /** The drawer has a mouse and nothing to swipe: its chips wrap instead of scrolling. */
  wrap?: boolean;
}

/**
 * « 5 nouvelles » then the meal's chips (#864), above the « Remplacer » list.
 * The first stays put, because asking for more is the point of the row; the
 * chips filter and may scroll under a thumb. Drawn by the phone's sheet and the
 * desktop drawer alike.
 */
export function FanChipBar({ chips, picked, moreCount, canRestart, onMore, onToggle, inset, wrap }: Props) {
  const { t } = useTranslation();
  const label = canRestart ? t('fanChips.restart') : t('fanChips.more', { count: moreCount });

  const chipList = chips.map((chip) => {
    const on = chip.id === picked;
    return (
      <Pressable
        key={chip.id}
        onPress={() => onToggle(chip.id)}
        style={[styles.chip, on && styles.chipOn]}
        accessibilityRole="button"
        accessibilityState={{ selected: on }}
        testID={`fan-chip-${chip.id}`}
      >
        <Text style={[styles.chipText, on && styles.chipTextOn]}>{t(chip.labelKey)}</Text>
      </Pressable>
    );
  });

  return (
    <View style={[styles.row, wrap && styles.rowWrap, { paddingLeft: inset, paddingRight: wrap ? inset : 0 }]} testID="fan-chips">
      {(moreCount > 0 || canRestart) && (
        <Pressable
          onPress={onMore}
          style={styles.more}
          accessibilityRole="button"
          accessibilityLabel={canRestart ? label : t('fanChips.moreLabel', { count: moreCount })}
          testID="fan-more"
        >
          <Feather name={canRestart ? 'rotate-ccw' : 'refresh-cw'} size={14} color={Paprika.primary} />
          <Text style={styles.moreText}>{label}</Text>
        </Pressable>
      )}
      {wrap ? chipList : (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.scroll}
          contentContainerStyle={[styles.scrollRow, { paddingRight: inset }]}
        >
          {chipList}
        </ScrollView>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingTop: 4,
    paddingBottom: 12,
  },
  rowWrap: {
    flexWrap: 'wrap',
    paddingTop: 0,
  },
  scroll: {
    flex: 1,
  },
  scrollRow: {
    gap: 8,
  },
  more: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 32,
    paddingHorizontal: 12,
    borderRadius: 100,
    backgroundColor: Paprika.accentLight,
  },
  moreText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    color: Paprika.primary,
  },
  chip: {
    justifyContent: 'center',
    height: 32,
    paddingHorizontal: 12,
    borderRadius: 100,
    backgroundColor: Paprika.surfaceHighest,
  },
  chipOn: {
    backgroundColor: Paprika.primary,
  },
  chipText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
  chipTextOn: {
    color: Paprika.onPrimary,
  },
});
